import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate, useTemplateFind } from "PFApp/hooks";
import { useHasKPIsAccess } from "PFApp/hooks";
import { isAdmin } from "PFCore/helpers/profile";
import { useIsShortlistingPermitted } from "PFCore/helpers/shortlists";
import { isFeatureEnabled } from "PFCore/helpers/use_is_feature_enabled";
import useIsPermittedTo, { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { FeatureFlag, PermissionRule } from "PFTypes";
import { Activity } from "PFTypes";
import { useMemo } from "react";

import { useActivityCurrentUserRole } from "./use_activity_current_user_role";
import { useActivityTemplateType } from "./use_activity_template_type";
import { useEngagementFeedbackParticipants } from "./use_engagement_feedback_participants";

export const useActivityPermissions = (activity: Activity) => {
  const { data: currentAccount } = useCurrentAccount();
  const { data: currentProfile } = useCurrentProfile();
  const {
    data: feedbackParticipants,
    isFetched: isFeedbackParticipantsFetched,
    isEnabled: isFeedbackParticipantsEnabled
  } = useEngagementFeedbackParticipants(activity);
  const findTemplate = useTemplateFind();

  const isPermittedTo = useIsPermittedTo(currentProfile);
  const hasKPIsAccess = useHasKPIsAccess();
  const isPermittedToManageShortlist = useIsCurrentUserPermittedTo(PermissionRule.ManageShortlist);

  const template = useTemplate(activity.template_id);
  const needMatches = template?.need_matches;

  const { isRole, isEngagement, isAudit } = useActivityTemplateType(activity);
  const { isResourcer, isReviewer } = useActivityCurrentUserRole(activity);
  const isPublicRole = !activity.private;

  const isShortlistingPermitted = useIsShortlistingPermitted()(activity);
  const roleKey = isAudit ? TemplateKey.AuditRole : TemplateKey.Role;

  // todo: [PROF-6072] allowing to see matches in the audit planner
  //  without being resourcer or audit_resourcer is most likely an invalid behaviour
  //  check: https://profinda.slack.com/archives/C02J0HP1ZRP/p1713272096105739?thread_ts=1712749315.559599&cid=C02J0HP1ZRP for details
  const canSeeMatches = useMemo(
    () => !!(needMatches && activity.coowned && (!isRole || isResourcer || isAudit)),
    [activity.coowned, isAudit, isResourcer, isRole, needMatches]
  );

  const canSeeShortlist = useMemo(() => {
    if (!isRole) {
      return false;
    }
    if (isReviewer) {
      return true;
    }
    return needMatches && (isShortlistingPermitted || (isPublicRole && isPermittedToManageShortlist));
  }, [isReviewer, isRole, isShortlistingPermitted, needMatches, isPublicRole, isPermittedToManageShortlist]);

  const canAddShortlist = isResourcer && isShortlistingPermitted;
  const canReviewShortlists = activity.coowned || isReviewer;

  const canSeeRoles = isEngagement;
  const canCreateRoles = useMemo(
    () => !!findTemplate({ key: roleKey, create_permitted: true, hidden: false }),
    [findTemplate, roleKey]
  );

  const canSeeVacancies = activity.coowned && isRole;

  const canSeeInterested = activity.coowned && isRole;

  const canSeeFeedback = !!(
    activity.coowned &&
    isEngagement &&
    feedbackParticipants &&
    feedbackParticipants?.meta.total > 0
  );

  const canSeeEconomics =
    activity.coowned &&
    isEngagement &&
    isFeatureEnabled(currentAccount)(FeatureFlag.EngagementEconomics) &&
    (activity.scenarios_count > 0 ||
      isPermittedTo(PermissionRule.EngagementEconomicsCreateScenarios) ||
      isPermittedTo(PermissionRule.EngagementEconomicsManage));

  const canSeeKPIs = isEngagement && hasKPIsAccess;

  const canSeeHistory =
    !!activity?.coowned || isAdmin(currentProfile) || isPermittedTo(PermissionRule.IntegrationUser);

  const canBecomeCoOwner =
    isResourcer && isPermittedTo(PermissionRule.AddYourselfAsOwner) && !activity.coowned;

  return {
    canSeeMatches,
    canSeeShortlist,
    canSeeVacancies,
    canSeeRoles,
    canCreateRoles,
    canSeeInterested,
    canSeeFeedback,
    canAddShortlist,
    canReviewShortlists,
    canSeeEconomics,
    canSeeKPIs,
    canSeeHistory,
    canBecomeCoOwner,
    permissionsReady: !isFeedbackParticipantsEnabled || isFeedbackParticipantsFetched
  };
};
